import { ReactElement, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { Button } from '@calabriocloud/beacon-ui'
import { useTranslation } from 'react-i18next'
import { PageContainer, HeaderSection, ContentSection } from './sections/layout'
import { ClassifierSection } from './sections/ClassifierSection'
import { InstructionsSection } from './sections/InstructionsSection'
import { initialValues, goalValidationSchema, Goal, statusKey } from '../constants'
import { useMetadataFieldsQuery } from './api'
import { ContactAssignmentSection } from './sections/ContactAssignmentSection'
import { NameSection } from './sections/NameSection'
import { StatusSection } from './sections/StatusSection'
import { Formik, Form, FormikHelpers } from 'formik'
import { useCreateGoal, useUpdateGoal, useContactGoalConfigQuery } from './api'
import { convertDateToDateValue, convertDateToString } from '../util'

export interface ViewGoalUser {
  id: number,
  personId: number,
  firstName: string,
  lastName: string,
  email: string,
  isSynchronized: boolean,
  isReconcileOnly: boolean,
  displayId: string,
  label: string,
  isSystemUser: boolean,

}
interface CreateNewGoalProps {
  initialValuesOverride?: Goal,
  goalId?: string,
  mode?: 'edit' | 'view' | 'clone' | 'create',
  isViewOnly?: boolean,
  isLegacyGoal?: boolean,
}
export const CreateNewGoal = ({
  initialValuesOverride,
  goalId,
  mode = 'create',
  isViewOnly = false,
  isLegacyGoal = false,
}: CreateNewGoalProps): ReactElement => {
  useMetadataFieldsQuery()
  const { t } = useTranslation()
  const history = useHistory()
  const [createGoal] = useCreateGoal()
  const [updateGoal] = useUpdateGoal()
  const handleCancel = () => {
    // TODO Potentially add a confirmation modal
    history.push('/admin/contactGoal')
  }

  const handleSubmit = async (values: Goal, formikHelpers: FormikHelpers<Goal>) => {
    try {
      // Clear field values that are tied to 'timeType'
      if (values.timeType === 4) {
        values.effectiveEndDate = null
        values.effectiveStartDate = null
      } else if (values.timeType === 5) {
        values.dueDate = null
      } else {
        values.effectiveEndDate = null
        values.effectiveStartDate = null
        values.dueDate = null
      }

      if (goalId && mode) {
        if (mode === 'clone') {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructuring assignment
          const { goalId, people, ...rest } = values
          await createGoal(rest)
        } else if (mode === 'edit' || mode === 'view') {
          // eslint-disable-next-line @typescript-eslint/no-unused-vars -- destructuring assignment
          const { people, ...rest } = values
          await updateGoal({ goalId, values: rest })
        } else {
          throw new Error('Not a valid mode')
        }
      } else {
        await createGoal(values)
      }

    } catch (error: Error | unknown) {
      if (error instanceof Error) {
        formikHelpers.setSubmitting(false)
        formikHelpers.setStatus({ isDisabled: isViewOnly, errorMessage: t(error.message) || t('somethingWentWrong') })
      }
    }
  }

  const initialValuesWithOverride = useMemo(() => ({
    ...initialValues,
    ...initialValuesOverride,
    name: mode === 'clone' ? `${initialValuesOverride?.name} (${t('Copy')})` : initialValuesOverride?.name || '',
    status: mode === 'clone' ? statusKey.indexOf('editable') : initialValuesOverride?.status ?? statusKey.indexOf('editable'),
    assigneeToWhoIds: isLegacyGoal ? (initialValuesOverride?.people || []).reduce<{ [key: string]: [] }>((acc, personId) => {
      acc[personId] = []
      return acc
    }, {}) : initialValuesOverride?.assigneeToWhoIds || {},
    dueDate: convertDateToString(convertDateToDateValue(initialValuesOverride?.dueDate ?? null) ?? null) || null,
    effectiveStartDate: convertDateToString(convertDateToDateValue(initialValuesOverride?.effectiveStartDate ?? null) ?? null) || null,
    effectiveEndDate: convertDateToString(convertDateToDateValue(initialValuesOverride?.effectiveEndDate ?? null) ?? null) || null,
    contactStartDate: convertDateToString(convertDateToDateValue(initialValuesOverride?.contactStartDate ?? null) ?? null) || null,
    contactEndDate: convertDateToString(convertDateToDateValue(initialValuesOverride?.contactEndDate ?? null) ?? null) || null,
  }), [])

  return (
    <PageContainer>
      <Formik
        initialValues={initialValuesWithOverride}
        initialStatus={{ isDisabled: isViewOnly, isLegacyGoal, errorMessage: undefined }}
        validationSchema={goalValidationSchema(t)}
        onSubmit={(values, formikHelpers) => handleSubmit(values, formikHelpers)}
        validateOnChange={true}
        validateOnBlur={true}
        validateOnMount={true}
      >
        {({ isSubmitting, dirty, ...props }) => {
          return (
            <Form aria-label={t('contactGoalForm')} id="goalForm" className="flex flex-col gap-4">
              <HeaderSection>
                <h1 className="text-3xl font-bold">{t('contactGoalAdministrationv2')}</h1>
                <div className='flex items-center gap-3 '>
                  {props.status.errorMessage && <div className="text-red-500 text-sm">{props.status.errorMessage}</div>}
                  <Button variant='secondary' data-testid='cancel-button' type='button' onPress={handleCancel}>{t('cancel')}</Button>
                  {mode !== 'clone' && <Button data-testid="save-button" type="submit" form="goalForm" isDisabled={isSubmitting || !dirty}>{isSubmitting ? t('submitting') : t('save')}</Button>}
                  {mode === 'clone' && <Button data-testid="clone-button" type="button" form="goalForm" isDisabled={isSubmitting || !dirty} onPress={() => handleSubmit(props.values, props)}>{t('Clone')}</Button>}
                </div>
              </HeaderSection>
              <ContentSection>
                <NameSection {...props} />
                <StatusSection />
                <ContactAssignmentSection {...props} />
                <ClassifierSection {...props} />
                <InstructionsSection />
              </ContentSection>
            </Form >
          )
        }}
      </Formik>
    </PageContainer>
  )
}

export const ViewGoal = () => {
  const { goalId, mode } = useParams<{ goalId: string, mode: 'edit' | 'view' | 'clone' }>()
  const { data: goalData, isLoading } = useContactGoalConfigQuery(goalId)
  const isViewOnly = goalId !== undefined && mode !== 'clone' && (goalData?.status === statusKey.indexOf('active') || goalData?.status === statusKey.indexOf('inactive'))
  const isLegacyGoal = Object.keys(goalData?.assigneeToWhoIds || {}).length === 0 && goalId !== undefined
  if (isLoading) return <div>Loading...</div>
  return (
    <CreateNewGoal
      initialValuesOverride={goalData ?? undefined}
      goalId={goalId}
      mode={mode}
      isViewOnly={isViewOnly}
      isLegacyGoal={isLegacyGoal}
    />
  )
}
